import { useState } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  TelemetryCards,
  TrafficOriginsPanel,
  AttackDistribution,
  AuthTimeSeries,
  NeverAuthenticatedDrawer,
  AttackerSpendDrawer,
} from "@/components/SecurityOverview";
import { useSecurityOverview, useSnapshotInfo } from "@/hooks/useDashboard.ts";
import { colors, eventsLightTheme } from "@/theme/eventsTokens.ts";

type SortField = "attempt_count" | "ip_count";
type PeriodDays = 3 | 7;

export default function SecurityOverviewPage() {
  const [days, setDays] = useState<PeriodDays>(7);
  const { data, isLoading, isFetching, error } = useSecurityOverview(days);
  const { data: snapshotInfo } = useSnapshotInfo();
  const [sortBy, setSortBy] = useState<SortField>("attempt_count");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [spendDrawerOpen, setSpendDrawerOpen] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load security overview. Please try again later.
      </Alert>
    );
  }

  if (!data) return null;

  const isRefreshing = isFetching && !isLoading;

  return (
    <ThemeProvider theme={eventsLightTheme}>
      <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <AuthTimeSeries data={data.timeseries} auditMode={data.audit_mode} />
        </Box>
        <Box sx={{ mb: 3, opacity: isRefreshing ? 0.5 : 1, transition: "opacity 0.2s ease" }}>
          <TelemetryCards
            data={data.telemetry}
            auditMode={data.audit_mode}
            neverAuthenticated={data.never_authenticated}
            neverAuthenticatedAsOf={data.never_authenticated_as_of}
            queryCoverageSignificant={data.query_coverage_significant}
            onNeverAuthClick={() => setDrawerOpen(true)}
            attackerSpend={data.attacker_spend ?? null}
            onAttackerSpendClick={() => setSpendDrawerOpen(true)}
          />
        </Box>
        <OverviewToolbar
          days={days}
          onDaysChange={setDays}
          sortBy={sortBy}
          onSortChange={setSortBy}
          isLoading={isRefreshing}
        />
        <Box sx={{ opacity: isRefreshing ? 0.5 : 1, transition: "opacity 0.2s ease" }}>
          <TrafficGrids data={data} sortBy={sortBy} snapshotLastUpdated={snapshotInfo?.last_updated} />
        </Box>
        <NeverAuthenticatedDrawer open={drawerOpen} asOf={data.never_authenticated_as_of} onClose={() => setDrawerOpen(false)} />
        <AttackerSpendDrawer open={spendDrawerOpen} onClose={() => setSpendDrawerOpen(false)} data={data.attacker_spend ?? null} />
      </Container>
    </ThemeProvider>
  );
}

const toggleSx = {
  fontSize: "0.75rem",
  fontWeight: 500,
  px: 1.5,
  py: 0.5,
  textTransform: "none",
  borderColor: colors.border,
  color: colors.textSecondary,
  "&.Mui-selected": {
    backgroundColor: colors.primary,
    color: colors.primaryForeground,
    borderColor: colors.primary,
    "&:hover": { backgroundColor: colors.primary },
  },
} as const;

const groupSx = { "& .MuiToggleButtonGroup-grouped": { border: `1px solid ${colors.border}` } };

function LabeledToggle<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: T;
  readonly options: readonly { value: T; label: string }[];
  readonly onChange: (v: T) => void;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 500 }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        onChange={(_, v: T | null) => { if (v !== null) onChange(v); }}
        sx={groupSx}
      >
        {options.map((o) => (
          <ToggleButton key={String(o.value)} value={o.value} sx={toggleSx}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

const PERIOD_OPTIONS = [
  { value: 3 as PeriodDays, label: "3 days" },
  { value: 7 as PeriodDays, label: "7 days" },
] as const;

const METRIC_OPTIONS = [
  { value: "attempt_count" as SortField, label: "Blocked attempts" },
  { value: "ip_count" as SortField, label: "IP count" },
] as const;

function OverviewToolbar({
  days,
  onDaysChange,
  sortBy,
  onSortChange,
  isLoading,
}: {
  readonly days: PeriodDays;
  readonly onDaysChange: (d: PeriodDays) => void;
  readonly sortBy: SortField;
  readonly onSortChange: (f: SortField) => void;
  readonly isLoading: boolean;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <LabeledToggle label="Period" value={days} options={PERIOD_OPTIONS} onChange={onDaysChange} />
        <Box sx={{ width: "1px", height: 20, backgroundColor: colors.border }} />
        <LabeledToggle label="Metric" value={sortBy} options={METRIC_OPTIONS} onChange={onSortChange} />
      </Box>
      {isLoading && (
        <LinearProgress
          sx={{
            mt: 1,
            height: 2,
            borderRadius: 1,
            backgroundColor: colors.borderLight,
            "& .MuiLinearProgress-bar": { backgroundColor: colors.accent },
          }}
        />
      )}
    </Box>
  );
}

function TrafficGrids({
  data,
  sortBy,
  snapshotLastUpdated,
}: {
  readonly data: NonNullable<ReturnType<typeof useSecurityOverview>["data"]>;
  readonly sortBy: SortField;
  readonly snapshotLastUpdated?: string;
}) {
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, xl: 6 }}>
        <AttackDistribution countries={data.hostile_countries} metric={sortBy} />
      </Grid>
      <Grid size={{ xs: 6, xl: 3 }}>
        <TrafficOriginsPanel
          title="Where Your Users Authenticate From"
          metricLabel="Successful Authentications"
          group={data.traffic_origins.legitimate}
          sortBy={sortBy}
          snapshotLastUpdated={snapshotLastUpdated}
        />
      </Grid>
      <Grid size={{ xs: 6, xl: 3 }}>
        <TrafficOriginsPanel
          title="Hostile Traffic Origins"
          metricLabel="Blocked Attempts"
          group={data.traffic_origins.hostile}
          variant="hostile"
          sortBy={sortBy}
          snapshotLastUpdated={snapshotLastUpdated}
        />
      </Grid>
    </Grid>
  );
}
